import { aspectValue, type AspectRatio, type OpLog } from '@panels/shared';
import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { prepareCanvas, renderOpLog } from '../lib/render.js';
import { buildTimeline, limitAt } from '../lib/replay.js';

export interface PanelCanvasHandle {
  /** Jump straight to the finished drawing. Tap-to-skip calls this. */
  finish: () => void;
  restart: () => void;
}

interface Props {
  ops: OpLog | null;
  aspectRatio: AspectRatio;
  /** Play the drawing back stroke-by-stroke on mount (§9 replay-on-view). */
  replay?: boolean;
  /** Delay before the replay starts, so a strip of panels draws in sequence. */
  replayDelayMs?: number;
  /**
   * When the replay starts, on the performance.now() timebase. Overrides
   * `replayDelayMs`. May be in the past: a co-viewed replay someone joins late
   * picks up at the right point (Round 3).
   */
  replayStartAt?: number;
  onReplayEnd?: () => void;
  className?: string;
  placeholder?: string;
}

/**
 * A panel, rendered from its op log. Optionally draws itself first.
 * Sizes to its container and renders at devicePixelRatio.
 */
export const PanelCanvas = forwardRef<PanelCanvasHandle, Props>(function PanelCanvas(
  { ops, aspectRatio, replay = false, replayDelayMs = 0, replayStartAt, onReplayEnd, className, placeholder },
  ref,
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ width: 0, height: 0 });
  const frameRef = useRef<number | null>(null);
  const doneRef = useRef(!replay);
  const endedRef = useRef(false);
  const onEndRef = useRef(onReplayEnd);
  onEndRef.current = onReplayEnd;

  const paint = (limit?: { op: number; point: number } | null) => {
    const canvas = canvasRef.current;
    const { width, height } = sizeRef.current;
    if (!canvas || width === 0) return;
    const ctx = prepareCanvas(canvas, width, height);
    if (!ctx) return;
    renderOpLog(ctx, ops, width, height, { limit: limit ?? undefined });
  };

  // Track the container width; the aspect ratio fixes the height.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const measure = () => {
      const width = wrap.clientWidth;
      const height = width / aspectValue(aspectRatio);
      if (width === sizeRef.current.width && height === sizeRef.current.height) return;
      sizeRef.current = { width, height };
      if (doneRef.current) paint();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectRatio]);

  const stop = () => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  };

  const finish = () => {
    stop();
    doneRef.current = true;
    paint();
    if (!endedRef.current) {
      endedRef.current = true;
      onEndRef.current?.();
    }
  };

  useImperativeHandle(ref, () => ({
    finish,
    restart: () => {
      endedRef.current = false;
      doneRef.current = false;
      start();
    },
  }));

  const start = () => {
    stop();
    const timeline = buildTimeline(ops);
    if (timeline.empty) {
      finish();
      return;
    }

    const startedAt = replayStartAt ?? performance.now() + replayDelayMs;
    const step = (now: number) => {
      const elapsed = now - startedAt;
      if (elapsed < 0) {
        // Still in the stagger delay: show an empty panel rather than the answer.
        paint({ op: 0, point: -1 });
        frameRef.current = requestAnimationFrame(step);
        return;
      }
      paint(limitAt(timeline, elapsed));
      if (elapsed >= timeline.duration) {
        finish();
        return;
      }
      frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    endedRef.current = false;
    if (replay) {
      doneRef.current = false;
      start();
    } else {
      doneRef.current = true;
      paint();
    }
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ops, replay, aspectRatio, replayStartAt]);

  return (
    <div
      ref={wrapRef}
      className={`panel-canvas ${className ?? ''}`}
      style={{ aspectRatio: aspectRatio.replace(':', ' / ') }}
    >
      <canvas ref={canvasRef} />
      {!ops && placeholder ? <span className="panel-placeholder">{placeholder}</span> : null}
    </div>
  );
});
